/**
 * CommandPlanner - what a player session sends, and when.
 *
 * The player says what it wants, cameras with their quality and the archive position, in
 * any order and at any time. The planner compares that with what the wire has and hands the
 * connection the commands that close the gap, one round at a time: a command is on the wire
 * until every camera it touches has settled - shown its first frame past the gate or been
 * answered by the server's reply - or the timeout gives up on it. Whatever the player asked for
 * meanwhile is simply the state the next round is planned from, so a burst of switches,
 * seeks and quality changes ends on the last of them, sent once.
 *
 * A switch carries the position for the cameras it brings; cameras that stay get a seek of
 * their own, as does a session whose cameras do not change. A quality change goes out when
 * nothing else differs and the wire is free, and holds nothing itself.
 */

import type { DesiredCamera, PlannedCommand } from '@/types/commandPlanner';
import type { StreamQuality } from '@/types/player';

interface PlannerOpts {
    /** Puts the command on the wire; false when the channel took nothing, so it stays due. */
    send: (command: PlannedCommand) => boolean;
    /** Whether the camera's track has arrived; a seek waits for a frame only of those. */
    hasStream: (deviceId: string) => boolean;
    /** How long a command may keep the next one waiting for its frames. */
    commandTimeoutMs: number;
}

/** The command on the wire: the cameras that still have to settle. */
interface InFlight {
    devices: Set<string>;
    timer: ReturnType<typeof setTimeout>;
    isRepositioning: boolean;
}

export class CommandPlanner {
    private readonly opts: PlannerOpts;

    /** deviceId → quality the player wants, in the player's order. */
    readonly desiredQualities = new Map<string, StreamQuality>();

    /** The archive position the player wants; null for live. */
    desiredPositionMs: number | null = null;

    /** Whether the wanted position lands on the exact time or rough on the keyframe before. */
    isDesiredSeekPrecise = true;

    isSeekForced = false;

    /** deviceId → quality the server was told, for the cameras the session carries. */
    readonly onWire = new Map<string, StreamQuality>();

    /** The position the server was told; null for live. */
    wirePositionMs: number | null = null;

    inFlight: InFlight | null = null;

    constructor(opts: PlannerOpts) {
        this.opts = opts;
    }

    /** The cameras the player wants, in order; index 0 identifies the session. */
    getDesiredIds(): string[] {
        return [...this.desiredQualities.keys()];
    }

    /** The quality the server was last told for the camera; null when it is not on the wire. */
    wireQuality(deviceId: string): StreamQuality | null {
        return this.onWire.get(deviceId) ?? null;
    }

    /** Whether a command is still on the wire. */
    get isActive(): boolean {
        return this.inFlight !== null;
    }

    // The inputs record a wish or a fact of the wire and send nothing: the caller plans when its
    // inputs of the moment are all in, so a position and the camera set it goes with leave in
    // one command.

    /** The whole set the player wants now, in its order. */
    setDesiredCameras(cameras: DesiredCamera[]): void {
        this.desiredQualities.clear();
        for (const camera of cameras) {
            this.desiredQualities.set(camera.deviceId, camera.quality);
        }
    }

    /** A new quality for one camera the player already wants. */
    setDesiredQuality(deviceId: string, quality: StreamQuality): void {
        if (this.desiredQualities.has(deviceId)) { this.desiredQualities.set(deviceId, quality); }
    }

    /** The position the player wants; null returns to live. */
    setDesiredPosition(positionMs: number | null, isPrecise: boolean): void {
        this.desiredPositionMs = positionMs;
        this.isDesiredSeekPrecise = isPrecise;
        // Live does not move on, and neither has a session still on its way to the position.
        const isPositionInFlight = !!this.inFlight?.isRepositioning &&
            positionMs === this.wirePositionMs;
        this.isSeekForced = positionMs !== null && !isPositionInFlight;
    }

    /** The session is at positionMs, by a command outside the planner (a positioned resume or
     *  step), and that is where the player wants it. */
    notePosition(positionMs: number): void {
        this.desiredPositionMs = positionMs;
        this.wirePositionMs = positionMs;
        this.isSeekForced = false;
    }

    /** A session opened live with these cameras, all at one quality. */
    onSessionOpened(deviceIds: string[], quality: StreamQuality): void {
        this.cancel();
        this.onWire.clear();
        for (const id of deviceIds) { this.onWire.set(id, quality); }
        this.wirePositionMs = null;
    }

    /** The session is gone, and the command on it with it. */
    onSessionClosed(): void {
        this.cancel();
        this.onWire.clear();
        this.wirePositionMs = null;
    }

    /** A camera of the command on the wire settled: it showed its frame, or the server's reply
     *  (an ack, or a report that no frame comes) accounted for it. */
    markDelivered(deviceId: string): void {
        if (!this.inFlight?.devices.delete(deviceId)) { return; }
        if (this.inFlight.devices.size === 0) { this.onDone(); }
    }

    /** The command on the wire is over: whatever the player asked for meanwhile goes out. */
    onDone(): void {
        if (!this.inFlight) { return; }
        this.cancel();
        this.plan();
    }

    private cancel(): void {
        if (this.inFlight) { clearTimeout(this.inFlight.timer); }
        this.inFlight = null;
    }

    /** Plans the next round from what is wanted and what the wire has; a no-op while a command
     *  is on the wire or the channel takes nothing. */
    plan(): void {
        if (this.inFlight || this.desiredQualities.size === 0) { return; }
        const desiredIds = this.getDesiredIds();
        const haveCamerasChanged = desiredIds.length !== this.onWire.size ||
            desiredIds.some((id) => !this.onWire.has(id));
        const hasPositionChanged = this.desiredPositionMs !== this.wirePositionMs;
        const awaitingIds = new Set<string>();
        let isSeekDue = hasPositionChanged || this.isSeekForced;
        let isRepositioning = false;
        if (haveCamerasChanged) {
            const cameras = [...this.desiredQualities]
                .map(([deviceId, quality]) => ({ deviceId, quality }));
            const camerasToAdd = cameras.filter((camera) => !this.onWire.has(camera.deviceId));
            const addedIds = camerasToAdd.map((camera) => camera.deviceId);
            const removedIds = [...this.onWire.keys()]
                .filter((id) => !this.desiredQualities.has(id));
            const wasSent = this.opts.send({
                kind: 'cameras',
                cameras,
                added: addedIds,
                removed: removedIds,
                positionMs: this.desiredPositionMs,
            });
            if (!wasSent) { return; }
            // The switch applied the quality of the cameras it brings; the others keep theirs.
            for (const id of removedIds) { this.onWire.delete(id); }
            for (const camera of camerasToAdd) {
                this.onWire.set(camera.deviceId, camera.quality);
                awaitingIds.add(camera.deviceId);
            }
            // No camera stayed to be seeked: the switch positioned the whole session.
            if (addedIds.length === desiredIds.length) {
                this.wirePositionMs = this.desiredPositionMs;
                this.isSeekForced = false;
                isSeekDue = false;
                isRepositioning = true;
            }
        }
        if (isSeekDue) {
            const positionMs = this.desiredPositionMs;
            const wasSent = this.opts.send(positionMs === null
                ? { kind: 'live' }
                : { kind: 'seek', positionMs, isPrecise: this.isDesiredSeekPrecise });
            if (!wasSent) { return; }
            this.wirePositionMs = positionMs;
            this.isSeekForced = false;
            isRepositioning = true;
            for (const id of this.onWire.keys()) {
                if (!this.opts.hasStream(id)) { continue; }
                awaitingIds.add(id);
            }
        } else if (!haveCamerasChanged) {
            // A quality change holds nothing: the camera plays on, only sharper or coarser.
            for (const [id, quality] of this.desiredQualities) {
                if (this.onWire.get(id) === quality) { continue; }
                if (this.opts.send({ kind: 'quality', deviceId: id, quality })) {
                    this.onWire.set(id, quality);
                }
            }
        }
        if (awaitingIds.size === 0) { return; }
        this.inFlight = {
            devices: awaitingIds,
            timer: setTimeout(() => this.onDone(), this.opts.commandTimeoutMs),
            isRepositioning,
        };
    }
}
